<script setup>
import { computed, defineEmits } from 'vue';
import {
  getEventFilterGroup,
  hasAdvanceRegistration,
  hasLivestream,
  isCalendarCurrentMonth,
} from '@/js/core/utils/calendar-events.js';
import {
  calendarEventListItems,
  calendarEventRangeInfo,
  calendarEventRangeKey,
  calendarEventScheduleLabel,
  isCalendarEventComplexSchedule,
} from '@/js/core/utils/calendar-event-projections.js';
import { EVENT_EXPIRY_PRECISION, isEventExpired } from '@/js/core/utils/event-expiry.js';
import CalendarPlusIcon from '@/js/core/components/icons/CalendarPlusIcon.vue';
import CalendarEventTime from '@/js/core/components/parts/calendar/CalendarEventTime.vue';
import EventAffiliationLine from '@/js/core/components/parts/calendar/EventAffiliationLine.vue';
import {
  calendarEventNavigationModeProp,
  useCalendarEventNavigation,
} from '@/js/core/composables/useCalendarEventNavigation.js';
import { DEFAULT_TIMEZONE } from '@/js/core/utils/timezone.js';
import { wrapBreakableTitle } from '@/js/core/utils/formatters.js';
import {
  eventAffiliationGroups,
  eventCourseFacultyLine,
} from '@/js/core/utils/event-affiliations.js';

const props = defineProps({
  events: {
    type: Array,
    default: () => [],
  },
  activeRange: {
    type: Object,
    default: () => ({}),
  },
  timezone: {
    type: String,
    default: DEFAULT_TIMEZONE,
  },
  showPastEvents: {
    type: Boolean,
    default: false,
  },
  canTogglePastEvents: {
    type: Boolean,
    default: true,
  },
  hideExpiredEvents: {
    type: Boolean,
    default: false,
  },
  eventFilterGroups: {
    type: Object,
    default: null,
  },
  eventFilterFallbackGroup: {
    type: String,
    default: 'Other',
  },
  emptyMessage: {
    type: String,
    default: 'There are no results in this month.',
  },
  eventNavigationMode: calendarEventNavigationModeProp,
});

const emit = defineEmits(['toggle-past-events']);

const togglePastEvents = () => emit('toggle-past-events');

const filteredList = computed(() => {
  return calendarEventListItems(props.events, {
    activeRange: props.activeRange,
    timezone: props.timezone,
    showPastEvents: props.showPastEvents,
    hideExpiredEvents: props.hideExpiredEvents,
  });
});

const isPastEvent = event =>
  isEventExpired(event, {
    timezone: props.timezone,
    precision: props.hideExpiredEvents ? EVENT_EXPIRY_PRECISION.time : EVENT_EXPIRY_PRECISION.day,
  });

const isPastRow = row => row.events.every(isPastEvent);

// Rows outside the month the view is anchored to name their own month. Month views
// never leave their month; a week span (the block's "upcoming") can.
const referenceMonthKey = computed(() => String(props.activeRange?.start_after || '').slice(0, 7));

const groupedRows = computed(() => {
  const singleDayByKey = {};
  const allKeys = new Set();

  for (const event of filteredList.value) {
    const key = calendarEventRangeKey(event, props.timezone);
    if (!singleDayByKey[key]) singleDayByKey[key] = [];
    singleDayByKey[key].push(event);
    allKeys.add(key);
  }

  const rows = [];
  for (const key of [...allKeys].sort()) {
    if (singleDayByKey[key]) {
      const rangeInfo = calendarEventRangeInfo(singleDayByKey[key][0], props.timezone, {
        referenceMonthKey: referenceMonthKey.value,
      });

      rows.push({
        rowKey: `range-${key}`,
        rangeInfo,
        events: singleDayByKey[key],
      });
    }
  }

  return rows;
});

const isCurrentMonth = computed(() => isCalendarCurrentMonth(props.activeRange, props.timezone));

const eventMetaLabels = event =>
  [hasLivestream(event) && 'Livestream', hasAdvanceRegistration(event) && 'Advance Registration']
    .filter(Boolean)
    .join(', ');

const scheduleLabel = event => calendarEventScheduleLabel(event, props.timezone);

const { eventNavigation, handleEventClick, handleEventKeydown } = useCalendarEventNavigation(props);
</script>

<template>
  <div v-if="props.canTogglePastEvents && isCurrentMonth" class="view-past-event-toggle">
    <button type="button" class="view-past-event-toggle__link" @click="togglePastEvents">
      {{ props.showPastEvents ? 'hide past events' : 'show past events' }}
    </button>
  </div>
  <div class="calendar-view-list-container">
    <div class="calendar-view-list">
      <div v-if="filteredList.length === 0" class="no-results">{{ props.emptyMessage }}</div>

      <div
        v-for="row in groupedRows"
        :key="row.rowKey"
        class="event-group-row"
        :class="[
          row.rangeInfo.isToday ? 'today' : 'not-today',
          { past: isPastRow(row) },
          { 'is-multi-day': row.rangeInfo.isRange },
        ]"
      >
        <div class="event-date-col">
          <span class="day-name">
            {{ row.rangeInfo.dayNameLabel }}
          </span>
          <span v-if="row.rangeInfo.isToday" class="today-label">Today</span>
          <span class="day-number">
            {{ row.rangeInfo.dayNumberLabel }}
          </span>
        </div>

        <div class="event-info-col">
          <div
            v-for="event in row.events"
            :key="event.id"
            class="event-item-detail"
            :class="{ past: isPastEvent(event) }"
            :data-filter-group="
              getEventFilterGroup(
                event.category,
                props.eventFilterGroups || undefined,
                props.eventFilterFallbackGroup
              )
            "
            :data-linkable="eventNavigation(event).isInteractive ? 'true' : undefined"
          >
            <div class="event-meta-top">
              <div class="event-meta-top-first-row">
                <span
                  v-if="isCalendarEventComplexSchedule(event, props.timezone)"
                  class="event-time event-time--multi-day"
                >
                  See details for schedule
                </span>
                <CalendarEventTime
                  v-else
                  :event="event"
                  :timezone="props.timezone"
                  class="event-time"
                />
                <a
                  v-if="event.ical_download_url"
                  :href="event.ical_download_url"
                  class="add-to-calendar-link"
                  :title="`Add to Calendar (${event.title})`"
                  :aria-label="`Add to Calendar (${event.title})`"
                  download
                >
                  <CalendarPlusIcon class="calendar-icon" />
                </a>
                <span v-if="eventMetaLabels(event)" class="event-meta-labels">
                  {{ eventMetaLabels(event) }}
                </span>
              </div>
              <div class="event-meta-top-second-row">
                <span class="event-type">{{ event.category }}</span>
                <span
                  v-for="audience in event.audience_labels || []"
                  :key="audience"
                  class="event-audience"
                >
                  {{ audience }}
                </span>
                <div v-if="eventCourseFacultyLine(event)" class="event-course-affiliation">
                  {{ eventCourseFacultyLine(event) }}
                </div>
              </div>
            </div>
            <component
              :is="eventNavigation(event).tag"
              class="event-link"
              :href="eventNavigation(event).href"
              :style="{
                cursor: eventNavigation(event).isInteractive ? 'pointer' : 'default',
              }"
              @click="handleEventClick($event, event)"
              @keydown="e => handleEventKeydown(e, event)"
            >
              <h2 class="event-title" v-html="wrapBreakableTitle(event.title)"></h2>
              <div class="event-meta-bottom">
                <EventAffiliationLine
                  class="event-affiliation"
                  :affiliations="eventAffiliationGroups(event)"
                />
                <span v-if="event.location" class="event-location">{{ event.location }}</span>
              </div>
              <span v-if="scheduleLabel(event)" class="visually-hidden">
                {{ scheduleLabel(event) }}
              </span>
            </component>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
